import { Col, Image, Input, Layout, Row, Spin, Tooltip, message, Checkbox } from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useEffect, useState, useRef } from "react";
import SquareAddCreditCard from "../components/SquareAddCreditCard";
import { useBrandContext } from "../contexts/Brand";
import { useServer } from "../contexts/Server";
import { useNavigate, useParams } from "react-router-dom";
import {
  decodeParamString,
  screenWidths,
  validateEmail,
  validatePassword,
} from "../const/const_functions";
import useWindowDimensions from "../hooks/useWindowDimensions";
import SuccessRegistration from "../components/SuccessRegistration";
import "../styles/CreateAccount.css";
import { FaInfoCircle } from "react-icons/fa";
import GlobalPaymentsCardEntry from "../components/GlobalPaymentsCardEntry";
import StripeCardRegister from "../components/StripeCardRegister";

const CreateAccount = () => {
  const { brandContextData } = useBrandContext();
  const { serverData } = useServer();
  const [passwordError, setPasswordError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { encodedStadiumId } = useParams();
  const stadiumId = decodeParamString(encodedStadiumId);
  const [stadium, setStadium] = useState(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [registrationDetails, setRegistrationDetails] = useState({
    password: "",
  });
  const [paymentProcessor, setPaymentProcessor] = useState(null);
  const [formToken, setFormToken] = useState(null);
  const [digitalFeeObj, setDigitalFeeObj] = useState(null);
  const [serviceFeeAgreement, setServiceFeeAgreement] = useState(false);
  const [stripeClientKey, setStripeClientKey] = useState(null);
  const [stripeSetupIntent, setStripeSetupIntent] = useState(null);
  const [stripeSetupStatus, setStripeSetupStatus] = useState(null);
  const [paymentCurrency, setPaymentCurrency] = useState('CAD');
  const [disableConfirm, setDisableConfirm] = useState(true);

  const navigate = useNavigate();

  // Refs (for synchronous access)
  const registrationDetailsRef = useRef({ password: "" });

  useEffect(() => {
    if (serverData) {
      getStadium();
      getServiceFeeInfo();
    }
  }, [serverData]);

  useEffect(() => {
    if (serviceFeeAgreement) {
      getPaymentProcessorInfo();
    }
  }, [serviceFeeAgreement]);

  useEffect(() => {
    if (serviceFeeAgreement && paymentProcessor === 'STRIPE') {
      createNewCustomerAndStripeIntent();
    }
  }, [serviceFeeAgreement, paymentProcessor]);

  useEffect(() => {
    if (stadium) {
      const errors = validateRegistrationDetails();
      const isValid = Object.keys(errors).length === 0;
      setDisableConfirm(!isValid);
    }
  }, [registrationDetails, stadium]);

  const getStadium = async () => {
    let stadium;
    try {
      stadium = await serverData.call("USSR_getOneStadium", stadiumId);
    } catch (e) {
      console.log(e);
    }

    // Only show this page for invoice stores
    if (!stadium?.stadTypes?.includes("STT_INVOICE")) {
      navigate("/404");
    }

    setStadium(stadium);
  };

  const disableSaveButton = () => {
    if (Object.keys(validateRegistrationDetails()).length) {
      return true;
    }
    return false;
  };

  const getPaymentProcessorInfo = async () => {
    try {
      const res = await serverData.call("MIKO_getPaymentProcessorAndFormToken", {
        contextId: brandContextData._id,
      });
      const { brandPaymentProcessor = 'SQ', formToken = null, brandSTCLK, paymentCurrency } = res;
      setPaymentProcessor(brandPaymentProcessor);
      setPaymentCurrency(paymentCurrency);

      if (brandPaymentProcessor === 'GP') {
        setFormToken(formToken);
      } else if (brandPaymentProcessor === 'STRIPE') {
        setStripeClientKey(brandSTCLK);
      }
    } catch (error) {
      console.log(error);
      message.error(
        `There was an error fetching account information. Please try creating it again`
      );
    }
  };

  const getServiceFeeInfo = async () => {
    let digitalFeeObj = {};
    try {
      digitalFeeObj = await serverData.call("MIKO_getContextServiceFee", {
        contextId: brandContextData._id
      });
      setDigitalFeeObj(digitalFeeObj)
    } catch (error) {
      console.log(error);
      return;
    }
  };

  // Used for square and global payments
  const createNewUser = async (token, verifiedBuyer) => {
    setLoading(true);

    const profile = {
      phone: registrationDetailsRef.current?.customerPhone
        ? registrationDetailsRef.current?.customerPhone
        : "",
      name: registrationDetailsRef.current.customerName,
      externalAccountId: registrationDetailsRef.current.accountId,
    };

    try {
      let userID = await serverData.call("BLLSTC_createPayor", {
        contextId: brandContextData._id,
        profile,
        username: registrationDetailsRef.current.customerEmail.trim(),
        stadiumId,
        passwordInput: true,
        password: registrationDetailsRef.current.password,
      });
      if (token && userID) {
        if (paymentProcessor === 'SQ') {
          await getSQCustomer(token, verifiedBuyer, userID);
        } else if (paymentProcessor === 'GP') {
          await getGPCustomer(token, verifiedBuyer, userID);
        }
      }
    } catch (e) {
      console.log(e);
      if (e?.message) {
        message.error(e.message);
      } else {
        message.error(
          `Sorry, there was an error creating your account. Please try again`
        );
      }
      setLoading(false);
    }
  };

  const createNewCustomerAndStripeIntent = async () => {
    if (paymentProcessor !== 'STRIPE') {
      return;
    }

    // Don't proceed if form validation fails
    if (disableConfirm) {
      message.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    const profile = {
      phone: registrationDetailsRef.current?.customerPhone
        ? registrationDetailsRef.current?.customerPhone
        : "",
      name: registrationDetailsRef.current.customerName,
      externalAccountId: registrationDetailsRef.current.accountId,
    };

    try {
      let userID = await serverData.call("BLLSTC_createPayor", {
        contextId: brandContextData._id,
        profile,
        username: registrationDetailsRef.current.customerEmail.trim(),
        stadiumId,
        passwordInput: true,
        password: registrationDetailsRef.current.password,
      });

      const stripeCustomer = await serverData.call("BLLSTC_getSQCustomer", {
        userID,
      });

      if (userID && stripeCustomer) {
        const { client_secret, status } = await serverData.call("MIKO_getStripeSetupIntent", { userID });
        setStripeSetupIntent(client_secret);
        setStripeSetupStatus(status);
      }
    } catch (e) {
      console.log(e);
      if (e?.message) {
        message.error(e.message);
      } else {
        message.error(
          `Sorry, there was an error creating your account. Please try again`
        );
      }
      setServiceFeeAgreement(false);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCardRegistered = (setupIntentData, isNextAction, nextActionDetails) => {
    // console.log('Card registered successfully:', setupIntentData);

    // // Extract the payment method ID
    // const paymentMethodId = setupIntentData.paymentMethod ||
    //   nextActionDetails?.setupIntent?.payment_method;

    // console.log(paymentMethodId);
    setSuccessModalVisible(true);
    setRegistrationDetails({ password: "" });
    setLoading(false);
  };

  const getSQCustomer = async (token, verifiedBuyer, userID) => {
    try {
      await serverData.call("BLLSTC_getSQCustomer", {
        userID,
      });
      createSQCustomerCard(token, verifiedBuyer, userID);
    } catch (e) {
      console.log(e);
      message.error(e.message);
      setLoading(false);
    }
  };

  const createSQCustomerCard = async (token, verifiedBuyer, userID) => {
    try {
      await serverData.call("BLLSTC_createSQCustomerCard", {
        userID,
        nonce: token.token,
        verifiedBuyer,
      });
      setSuccessModalVisible(true);
      setRegistrationDetails({ password: "" });
    } catch (e) {
      console.log(e);
      message.error(e.message);
      setLoading(false);
    }
    setLoading(false);
  };

  const getGPCustomer = async (token, verifiedBuyer, userID) => {
    try {
      await serverData.call("BLLSTC_getSQCustomer", {
        userID,
      });
      createGPCustomerCard(token, verifiedBuyer, userID);
    } catch (e) {
      console.log(e);
      message.error(e.message);
      setLoading(false);
    }
  };

  const createGPCustomerCard = async (token, verifiedBuyer, userID) => {
    try {
      await serverData.call("MIKO_createGPPaymentMethod", {
        userID,
        nonce: token.token,
      });
      setSuccessModalVisible(true);
      setRegistrationDetails({ password: "" });
    } catch (e) {
      console.log(e);
      message.error(e.message);
      setLoading(false);
    }
    setLoading(false);
  };

  let mobile = false;
  const { width } = useWindowDimensions();
  if (screenWidths.md > width) {
    mobile = true;
  }

  let imgWidth = 180;

  if (
    stadium?._id === "vWbhEfxtsfbK82CKv" ||
    stadium?._id === "CakDLtXvDiB7xZRCR"
  ) {
    imgWidth = 280;
  }

  const closeSuccessModal = () => {
    setSuccessModalVisible(false);
  };

  const _renderFormField = (formObj, fieldName) => {
    const { label } = formObj;
    const formLabel = label;
    const txtLabel = (
      <p style={{ fontWeight: "600", color: "black", marginBottom: 6 }}>
        {formLabel}
        <span style={{ fontWeight: "500", color: "red", marginBottom: 6 }}>
          *
        </span>
      </p>
    );

    return (
      <>
        {txtLabel}
        <Input
          type={fieldName === "customerPhone" ? "number" : "text"}
          value={registrationDetails[fieldName]}
          onChange={(e) => takeInput({ inputVal: e?.target?.value, fieldName })}
        />
        <div style={{ height: 18 }}>
          {validateRegistrationDetails()[fieldName] &&
            registrationDetails[fieldName] ? (
            <p style={{ color: "red", fontSize: 12 }}>
              {validateRegistrationDetails()[fieldName]}
            </p>
          ) : null}
        </div>
      </>
    );
  };

  const validateRegistrationDetails = () => {
    const errors = {};
    if (Object.keys(registrationDetails).length === 0) {
      return { noRegistrationDetails: "There are no registration details" };
    }

    if (
      !registrationDetails.password.trim() ||
      registrationDetails.password.length < 6
    ) {
      errors["password"] = "Please enter a valid password";
    }

    Object.keys(stadium?.invoiceTemplate).forEach((field) => {
      const { type, sibling, display, label, canRegister } =
        stadium.invoiceTemplate[field];

      if (display && canRegister) {
        const value = registrationDetails[field];

        if (field === "customerEmail") {
          const trimmedValue = value?.trim();
          if (!trimmedValue || trimmedValue === "") {
            errors[field] = `${label} is required.`;
          } else {
            if (!validateEmail(trimmedValue)) {
              errors[field] = `Please enter a valid email.`;
            }
          }
        } else if (field === "customerPhone") {
          if (!value || String(value).trim() === "") {
            errors[field] = `${label} is required.`;
          } else {
            if (value.length !== 10) {
              errors[field] = `Phone numbers must be 10 digits long.`;
            }
          }
        } else {
          if (!value || String(value).trim() === "") {
            errors[field] = `${label} is required.`;
          }
        }
      }
    });

    return errors;
  };

  const takeInput = ({ inputVal, fieldName }) => {
    setRegistrationDetails((prevState) => ({
      ...prevState,
      [fieldName]: inputVal,
    }));

    registrationDetailsRef.current = {
      ...registrationDetailsRef.current,
      [fieldName]: inputVal,
    }
  };

  if (!stadium) {
    return null;
  }

  const formFields = [];
  for (const [key, value] of Object.entries(stadium?.invoiceTemplate)) {
    const newObj = { ...value, key };

    if (newObj.display && newObj.canRegister) {
      formFields.push(newObj);
    }
  }

  // Sort the formFields array based on the renderPosition property
  formFields.sort((a, b) => (a.renderPosition || 0) - (b.renderPosition || 0));

  const tooltipText = "Your credit card is saved by a secure third-party provider. Your personal and financial information is secure and PCI DSS compliant when saved through our registration portal";
  const feeLabel = String(digitalFeeObj?.serviceFeeInfo?.feeLabel ? digitalFeeObj.serviceFeeInfo.feeLabel : "Service Fee").toLowerCase();
  const feePrefix = ['a', 'e', 'i', 'o', 'u'].includes(feeLabel[0]) ? 'an' : 'a';

  return (
    <Layout style={{ backgroundColor: "white", minHeight: "100vh" }}>
      <SuccessRegistration
        successModalVisible={successModalVisible}
        closeSuccessModal={closeSuccessModal}
        stadiumName={stadium?.name}
      />
      <Spin
        size="large"
        spinning={loading}
        tip="Registering your account... Please do not navigate away or refresh the page."
      >
        <Content
          style={{
            marginTop: 20,
            marginBottom: 100,
            flexDirection: "column",
            display: "flex",
          }}
        >
          <Col xs={{ span: 22, offset: 1 }} md={{ span: 12, offset: 6 }}>
            {stadium?.imageUrl ? (
              <Row justify={"center"}>
                <Image
                  width={mobile ? "50%" : imgWidth}
                  preview={false}
                  src={stadium.imageUrl}
                  style={{
                    maxHeight: 180,
                    minHeight: 75,
                    objectFit: "contain",
                  }}
                />
              </Row>
            ) : null}

            <p
              style={{
                fontWeight: "600",
                color: "black",
                fontSize: 24,
                marginBottom: 0,
              }}
            >
              Register Your Account
            </p>
            <p
              style={{
                fontWeight: "500",
                color: "grey",
                marginBottom: 40,
                fontSize: 14,
                marginTop: 4,
              }}
            >
              Already Registered?{" "}
              <a href="/login">Edit/Update Your Account Information Here</a>
            </p>
            {formFields.map((field) => (
              <React.Fragment key={field.key}>
                {_renderFormField(field, field.key)}
              </React.Fragment>
            ))}
            <p style={{ fontWeight: "600", color: "black", marginBottom: 6 }}>
              Password
              <span
                style={{
                  fontWeight: "500",
                  color: "red",
                  marginBottom: 6,
                  marginRight: 4,
                }}
              >
                *
              </span>
              <Tooltip
                title={`We require a password so you may login and update your auto-billing info at a later date if necessary.`}
              >
                <FaInfoCircle
                  name="info"
                  style={{ fontSize: 15, color: "gray" }}
                />
              </Tooltip>
            </p>
            <Input
              value={registrationDetails.password}
              defaultValue={null}
              type="password"
              onChange={(e) => {
                if (validatePassword(e.target.value) || !e.target.value) {
                  takeInput({
                    inputVal: e.target.value,
                    fieldName: "password",
                  });
                  setPasswordError(false);
                } else {
                  takeInput({
                    inputVal: e.target.value,
                    fieldName: "password",
                  });
                  setPasswordError(true);
                }
              }}
              maxLength={36}
            />
            <div style={{ height: 18 }}>
              {passwordError ? (
                <p style={{ color: "red", fontSize: 12 }}>
                  Passwords must be at least 6 characters long
                </p>
              ) : null}
            </div>
            {(digitalFeeObj?.applyServiceFee && (digitalFeeObj?.serviceFeeInfo && digitalFeeObj?.serviceFeeInfo?.feeAcknowledgement)) ? (
              <div style={{ paddingTop: 10, display: "flex", alignItems: "center", gap: "8px" }}>
                <Checkbox
                  onChange={(e) => setServiceFeeAgreement(e.target.checked)}
                  style={{
                    "--background-color": "black",
                    "--border-color": "black",
                    color: "gray",
                  }}
                  disabled={disableConfirm}
                  checked={serviceFeeAgreement}
                >
                  By checking this box, I acknowledge that all transactions are subject to {feePrefix} {feeLabel}
                  <span style={{ color: "red" }}>*</span>
                </Checkbox>
                <Tooltip title={tooltipText}>
                  <FaInfoCircle style={{ fontSize: 15, color: "gray" }} />
                </Tooltip>
              </div>
            ) :
              <div style={{ paddingTop: 10, display: "flex", alignItems: "center", gap: "8px" }}>
                <Checkbox
                  onChange={(e) => setServiceFeeAgreement(e.target.checked)}
                  style={{
                    "--background-color": "black",
                    "--border-color": "black",
                    color: "gray",
                  }}
                  disabled={disableConfirm}
                  checked={serviceFeeAgreement}
                >
                  {tooltipText}, Please click the checkbox to proceed.
                  <span style={{ color: "red" }}>*</span>
                </Checkbox>
              </div>
            }
            <>
              {
                (serviceFeeAgreement) ?
                  <div style={{ paddingTop: 10 }}>
                    <>
                      {paymentProcessor === 'SQ' && (
                        <SquareAddCreditCard
                          tempName={registrationDetails.customerName}
                          tempEmail={registrationDetails.customerEmail?.trim()}
                          tempPhone={""}
                          disableSaveButton={disableSaveButton}
                          handleSubmitCreditCard={createNewUser}
                        />
                      )}
                      {paymentProcessor === 'GP' && (
                        <GlobalPaymentsCardEntry
                          makeThePayment={createNewUser}
                          formToken={formToken}
                          maxwidth={true}
                          formType={"storage"}
                        />
                      )}
                      {paymentProcessor === 'STRIPE' && stripeSetupIntent && (
                        <StripeCardRegister
                          clientToken={stripeClientKey}
                          paymentCurrency={paymentCurrency}
                          registerTheCard={handleCardRegistered}
                          clientSecret={stripeSetupIntent}
                          setupStatus={stripeSetupStatus}
                          maxWidth={false}
                        />
                      )}
                    </>
                  </div>
                  :
                  null
              }
            </>
          </Col>
        </Content>
      </Spin>
    </Layout>
  );
};

export default CreateAccount;